/* Mobile sticky add-to-cart bar (growth plan B1).
   Visible whenever the real buy box is off-screen in either direction, so a
   phone visitor has a price and a buy button in the first frame instead of
   only after scrolling past a buy box that sits two screens down (product
   listing audit 2026-07-28, Phase 1 Step 5). Mirrors the real buy button's
   price/availability so the bar can never disagree with the buy box.
   Duo-options products scroll back to the configurator instead of adding. */
if (!customElements.get('duo-sticky-atc')) {
  customElements.define(
    'duo-sticky-atc',
    class DuoStickyAtc extends HTMLElement {
      connectedCallback() {
        // #MainContent > .shopify-section wrappers are z-index:1 stacking
        // contexts (theme.liquid layering strip), so a fixed bar rendered
        // inside main-product paints UNDER later sibling sections (the
        // reviews panel washed right over it). Move to <body> so the bar's
        // own z-index applies at the root — the same escape the review
        // photo lightbox uses (duo-review-form.js). Stale bars are cleared
        // first so a theme-editor section re-render can't stack two.
        if (this.parentElement !== document.body) {
          document.querySelectorAll('body > duo-sticky-atc').forEach((stale) => stale.remove());
          document.body.appendChild(this);
          return; // appendChild reconnects; setup runs on the second call
        }
        this.button = this.querySelector('.duo-sticky-atc__button');
        this.priceEl = this.querySelector('[data-duo-sticky-price]');
        const sectionId = this.dataset.sectionId;
        this.submit = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.priceSource = document.getElementById(`price-${sectionId}`);
        this.hasOptions = this.hasAttribute('data-has-options');

        this.button.addEventListener('click', () => this.onClick());

        const watchTarget = this.submit || this.priceSource;
        if (!watchTarget || !('IntersectionObserver' in window)) return;
        // The bar covers the bottom strip of the viewport, so shorten the
        // observer's root by the bar's own height: the real buy button only
        // counts as in view once it has cleared the bar. Without this the two
        // trade places over the same pixels as the button enters from below.
        // Height reads 0 above 750px, where the bar is display:none anyway.
        const barHeight = Math.round(this.offsetHeight) || 64;
        this.io = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.setVisible(!entry.isIntersecting);
          },
          { rootMargin: `0px 0px -${barHeight}px 0px` }
        );
        this.io.observe(watchTarget);

        this.sync();
        this.observer = new MutationObserver(() => this.sync());
        if (this.priceSource) {
          this.observer.observe(this.priceSource, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
          });
        }
        if (this.submit) {
          this.observer.observe(this.submit, { attributes: true, childList: true, subtree: true });
        }
      }

      setVisible(visible) {
        // The observer's first callback describes the page as it loaded, with
        // no scrolling behind it. Settle into that state without the slide, so
        // the bar is simply present on arrival rather than animating in over a
        // page that is still painting; every later change still transitions.
        if (this.settled) {
          this.classList.toggle('duo-sticky-atc--visible', visible);
          return;
        }
        this.settled = true;
        this.classList.add('duo-sticky-atc--instant');
        this.classList.toggle('duo-sticky-atc--visible', visible);
        void this.offsetHeight; // flush the untransitioned state before restoring
        this.classList.remove('duo-sticky-atc--instant');
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        if (this.observer) this.observer.disconnect();
      }

      sync() {
        if (this.priceSource && this.priceEl) {
          const onSale = !!this.priceSource.querySelector('.price--on-sale');
          const item = this.priceSource.querySelector(
            onSale ? '.price-item--sale' : '.price-item--regular'
          );
          if (item) this.priceEl.textContent = item.textContent.trim();
        }
        if (this.submit && !this.hasOptions) {
          this.button.disabled = this.submit.disabled;
          const label = this.submit.querySelector('span');
          if (label && label.textContent.trim()) {
            this.button.textContent = label.textContent.trim();
          }
        }
      }

      onClick() {
        if (!this.hasOptions && this.submit && !this.submit.disabled) {
          this.submit.click();
          return;
        }
        const target = document.querySelector('duo-product-options') || this.submit;
        if (!target) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
    }
  );
}
